/* eslint-disable global-require */

const assert = require('assert');
const assign = require('lodash/assign');
const defaults = require('lodash/defaults');
const clone = require('lodash/clone');

const eventing = require('../../helpers/eventing.js');
const Errors = require('../Errors.js');

module.exports = function processPubOptionsFactory(deps = {}) {
  [
    'deviceHelpers',
    'getUserMediaHelper',
  ].forEach((key) => {
    assert(deps[key], `${key} dependency must be injected into processPubOptions`);
  });

  const generateConstraintInfo = deps.generateConstraintInfo || require('./generateConstraintInfo.js');
  const windowMock = deps.global || global;
  const logging = deps.logging || require('../logging.js');
  const navigator = deps.navigator || windowMock.navigator;
  const OTHelpers = deps.OTHelpers || require('../../common-js-helpers/OTHelpers.js');
  const OTPlugin = deps.OTPlugin || require('../../otplugin/otplugin.js');
  const screenSharing = deps.screenSharing || require('../screensharing/screen_sharing.js')();
  const usingOptionalMandatoryStyle = require('./usingOptionalMandatoryStyle')({ navigator: navigator || {} });

  const {
    deviceHelpers,
    getUserMediaHelper,
  } = deps;

  const isMediaStreamTrack = source => (
    source &&
    typeof source === 'object' &&
    typeof source.kind === 'string' &&
    typeof source.stop === 'function' &&
    typeof source.enabled === 'boolean'
  );

  const normaliseOptions = (originalOptions, logPrefix) => {
    const options = originalOptions && typeof originalOptions === 'object' ?
      clone(originalOptions) : {};

    if (options.audioSource === false) {
      options.audioSource = null;
    }

    if (options.videoSource === false) {
      options.videoSource = null;
    }

    // Ignore minVideoBitrate if it's defined but not a positive number
    if (
      options.minVideoBitrate !== undefined &&
      !(
        typeof options.minVideoBitrate === 'number' &&
        options.minVideoBitrate > 0
      )
    ) {
      logging.warn(
        `${logPrefix}: Invalid minVideoBitrate in publisher properties. minVideoBitrate must ` +
        'be a positive number'
      );

      delete options.minVideoBitrate;
    }

    // Ignore audioBitrate if it's defined but not between 6kb and 510kb
    if (
      options.audioBitrate !== undefined &&
      !(
        options.audioBitrate >= 6 * 1000 &&
        options.audioBitrate <= 510 * 1000
      )
    ) {
      logging.warn(
        `${logPrefix}: Invalid audioBitrate in publisher properties. audioBitrate must ` +
        `be a number between ${6 * 1000} and ${510 * 1000} bits per second`
      );

      delete options.audioBitrate;
    }

    const { env } = OTHelpers;

    // Crashes in FF45-46 and fails to publish in FF47-48
    if (env.name === 'IE' || (env.name === 'Firefox' && env.version < 49)) {
      const label = env.name === 'IE' ?
        'Internet Explorer' :
        'Firefox 48 and below';
      if (isMediaStreamTrack(options.audioSource)) {
        options.audioSource = null;
        logging.warn(`${logPrefix}: Cannot use custom tracks with ${label}. Setting audioSource to null`);
      }
      if (isMediaStreamTrack(options.videoSource)) {
        options.videoSource = null;
        logging.warn(`${logPrefix}: Cannot use custom tracks with ${label}. Setting videoSource to null`);
      }
    }

    return options;
  };

  const processPubOptions = (originalOptions, logPrefix, shouldStop) => {
    const options = normaliseOptions(originalOptions, logPrefix);
    const isScreenSharing = (
      options.videoSource === 'screen' ||
      options.videoSource === 'window' ||
      options.videoSource === 'tab' ||
      options.videoSource === 'browser' ||
      options.videoSource === 'application'
    );
    const isCustomAudioTrack = isMediaStreamTrack(options.audioSource);
    const isCustomVideoTrack = isMediaStreamTrack(options.videoSource);

    // getUserMedia is required if either audio or video is: a) not custom and b) not disabled
    const isAudioGumRequired = !isCustomAudioTrack && options.audioSource !== null;
    const isVideoGumRequired = !isCustomVideoTrack && options.videoSource !== null;
    const isGumRequired = isAudioGumRequired || isVideoGumRequired;

    // We display audio level and allow audio fallback if we're not screensharing
    // or if we're screensharing but with a custom audio track
    const shouldAllowAudio = (isScreenSharing && isCustomAudioTrack) || !isScreenSharing;

    // We whitelist property keys for logging purposes
    const validProperties = [
      'audioDeviceId',
      'audioFallbackEnabled',
      'audioSource',
      'channels',
      'classNames',
      'constraints',
      'disableAudioProcessing',
      'enableRenegotiation',
      'enableStereo',
      'facingMode',
      'fitMode',
      'frameRate',
      'height',
      'insertDefaultUI',
      'insertMode',
      'audioBitrate',
      'maxResolution',
      'minVideoBitrate',
      'mirror',
      'name',
      'publishAudio',
      'publishVideo',
      'resolution',
      'showControls',
      'style',
      'usePreviousDeviceSelection',
      'videoDeviceId',
      'videoDimensions',
      'videoSource',
      'width',
    ];

    const properties = defaults(options, {
      mirror: !isScreenSharing && !isCustomVideoTrack,
      publishAudio: true,
      publishVideo: true,
      showControls: true,
      fitMode: isScreenSharing ? 'contain' : 'cover',
      audioFallbackEnabled: shouldAllowAudio,
      insertDefaultUI: true,
      enableRenegotiation: false,
      enableStereo: false,
      disableAudioProcessing: false,
    });

    if (properties.name) {
      properties.name = String(properties.name);
    }

    if (!properties.constraints) {
      const constraintInfo = generateConstraintInfo({
        isScreenSharing,
        isCustomAudioTrack,
        isCustomVideoTrack,

        audioSource: properties.audioSource,
        publishAudio: properties.publishAudio,

        videoSource: properties.videoSource,
        publishVideo: properties.publishVideo,
        resolution: properties.resolution,
        maxResolution: properties.maxResolution,
        frameRate: properties.frameRate,
        facingMode: properties.facingMode,

        enableRenegotiation: properties.enableRenegotiation,
        enableStereo: properties.enableStereo,
        disableAudioProcessing: properties.disableAudioProcessing,
        env: OTHelpers.env,

        usingOptionalMandatoryStyle: usingOptionalMandatoryStyle(isScreenSharing),
      });

      assign(properties, constraintInfo);
    } else {
      logging.warn(`${logPrefix}: You have passed your own constraints not using ours`);
    }

    if (isGumRequired && Object.prototype.hasOwnProperty.call(properties, 'usePreviousDeviceSelection')) {
      if (OTHelpers.env.protocol !== 'https:') {
        logging.warn(`${logPrefix}: https:// is required for usePreviousDeviceSelection`);
      }
      if (OTHelpers.env.name !== 'IE') {
        logging.warn(`${logPrefix}: usePreviousDeviceSelection only works in Internet Explorer`);
      }
      if (Object.prototype.hasOwnProperty.call(OTPlugin, 'settings')) {
        // If the usePreviousDeviceSelection property is passed in then use that
        OTPlugin.settings.usePreviousDeviceSelection = properties.usePreviousDeviceSelection;
      }
    }

    const processedOptions = {
      isScreenSharing,
      isCustomAudioTrack,
      isCustomVideoTrack,
      shouldAllowAudio,
      properties,
      validProperties,
      releases: [],
    };

    eventing(processedOptions);

    processedOptions.releaseAll = () => {
      while (processedOptions.releases.length > 0) {
        const release = processedOptions.releases.shift();
        release();
      }
    };

    processedOptions.getUserMedia = () => {
      const onAccessDialogOpened = (...args) => processedOptions.emit('accessDialogOpened', ...args);
      const onAccessDialogClosed = (...args) => processedOptions.emit('accessDialogClosed', ...args);

      const onConstraintsFound = (constraints) => {
        logging.debug(`${logPrefix}: onConstraintsFound`, constraints);

        if (shouldStop()) {
          throw new Error('Publisher already destroyed, stopping getUserMedia');
        }

        const gumRequest = getUserMediaHelper(constraints);
        processedOptions.releases.push(gumRequest.release);

        gumRequest.on('accessDialogOpened', onAccessDialogOpened);
        gumRequest.on('accessDialogClosed', onAccessDialogClosed);

        const cancelRelease = () => {
          const index = processedOptions.releases.indexOf(gumRequest.release);
          if (index >= 0) {
            processedOptions.releases.splice(index, 1);
          }
          gumRequest.off('accessDialogOpened', onAccessDialogOpened);
          gumRequest.off('accessDialogClosed', onAccessDialogClosed);
          gumRequest.release();
        };

        return gumRequest.promise.then(
          (stream) => {
            cancelRelease();

            if (isCustomAudioTrack) {
              stream.addTrack(options.audioSource);
            }
            if (isCustomVideoTrack) {
              stream.addTrack(options.videoSource);
            }
            return stream;
          },
          (err) => {
            cancelRelease();

            if (err.name === Errors.USER_MEDIA_ACCESS_DENIED) {
              processedOptions.emit('accessDenied', err);
            } else {
              processedOptions.emit('streamAvailableError', err);
            }
            throw err;
          }
        );
      };

      if (!isGumRequired) {
        const stream = new windowMock.MediaStream();
        if (isCustomAudioTrack) {
          stream.addTrack(options.audioSource);
        }
        if (isCustomVideoTrack) {
          stream.addTrack(options.videoSource);
        }
        return Promise.resolve(stream);
      }

      if (isScreenSharing) {
        return screenSharing.getConstraints({
          onAccessDialogOpened,
          onAccessDialogClosed,
          videoSource: options.videoSource,
          constraints: properties.constraints,
        }).then(
          (constraints) => {
            properties.constraints = constraints;
            return onConstraintsFound(properties.constraints);
          },
          (err) => {
            if (err.name === Errors.USER_MEDIA_ACCESS_DENIED) {
              processedOptions.emit('accessDenied', err);
            } else {
              processedOptions.emit('screenSharingError', err);
            }
            throw err;
          }
        );
      }

      return deviceHelpers.shouldAskForDevices().then((devices) => {
        logging.debug(`${logPrefix}: shouldAskForDevices:`, devices);
        if (!devices.video) {
          logging.warn(`${logPrefix}: Setting video constraint to false, there are no video sources`);
          properties.constraints.video = false;
        }
        if (!devices.audio) {
          logging.warn(`${logPrefix}: Setting audio constraint to false, there are no audio sources`);
          properties.constraints.audio = false;
        }
        processedOptions.videoDevices = devices.videoDevices;
        processedOptions.audioDevices = devices.audioDevices;
        return onConstraintsFound(properties.constraints);
      });
    };

    return processedOptions;
  };

  return processPubOptions;
};



// WEBPACK FOOTER //
// ./src/js/ot/publisher/processPubOptions.js